import { saveTool, ToolOptions } from "../../api/tooling";

export type ToolInput = Parameters<typeof saveTool>[0];
export type ImportRow = { row: number; input: ToolInput; errors: string[] };
export const importHeaders = [
  "type",
  "operation",
  "part_ids",
  "location_id",
  "details",
];

// CSV state machine: quoted commas, escaped quotes, and multiline cells are valid.
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [],
    cell = "",
    quoted = false,
    closed = false;
  const pushCell = () => {
    row.push(cell);
    cell = "";
    closed = false;
  };
  const pushRow = () => {
    pushCell();
    rows.push(row);
    row = [];
  };
  text = text.replace(/^\uFEFF/, "");
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (quoted) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else {
          quoted = false;
          closed = true;
        }
      } else cell += c;
    } else if (c === ",") pushCell();
    else if (c === "\r" || c === "\n") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      pushRow();
    } else if (c === '"' && !cell && !closed) quoted = true;
    else {
      if (closed || c === '"')
        throw new Error(
          "Invalid CSV quoting. Save the template as CSV and try again."
        );
      cell += c;
    }
  }
  if (quoted) throw new Error("The CSV contains an unclosed quoted cell.");
  if (cell || row.length || closed) pushRow();
  return rows;
}

export function validateImport(
  text: string,
  options: ToolOptions
): ImportRow[] {
  const [header = [], ...data] = parseCsv(text);
  const headers = header.map((h) => h.trim().toLowerCase());
  if (
    headers.length !== importHeaders.length ||
    new Set(headers).size !== headers.length ||
    importHeaders.some((h) => !headers.includes(h))
  ) {
    throw new Error(`Use the template columns: ${importHeaders.join(", ")}.`);
  }
  const partIds = new Set(options.parts.map((p) => p.value));
  const locations = new Set((options.locations || []).map((l) => l.value));
  const seen = new Set<string>();
  const rows = data.flatMap((cells, index): ImportRow[] => {
    if (cells.every((c) => !c.trim())) return [];
    const get = (name: string) => (cells[headers.indexOf(name)] || "").trim();
    const errors: string[] = [];
    if (cells.length !== headers.length)
      errors.push("Incorrect number of columns");
    const rawType = get("type").toLowerCase();
    const type = options.types.find(
      (t) =>
        t.value.toLowerCase() === rawType || t.label.toLowerCase() === rawType
    )?.value;
    if (!type) errors.push("Unknown tool type");
    const operation = get("operation").toLowerCase();
    if (!["cutting", "bending"].includes(operation))
      errors.push("Operation must be cutting or bending");
    const rawParts = get("part_ids")
      .split(/[;,]/)
      .map((p) => p.trim());
    const parts = rawParts.map(Number);
    if (
      rawParts.some((p) => !/^\d+$/.test(p)) ||
      parts.some((id) => !Number.isSafeInteger(id) || !partIds.has(id))
    )
      errors.push("Provide valid linked part IDs separated by semicolons");
    if (new Set(parts).size !== parts.length)
      errors.push("Repeated linked part ID");
    const location = get("location_id");
    const locationId = location ? Number(location) : null;
    if (
      location &&
      (!/^\d+$/.test(location) || !locations.has(Number(location)))
    )
      errors.push("Unknown location ID");
    const details = get("details");
    if (details.length > 10000) errors.push("Details exceed 10,000 characters");
    const input = {
      type: type || rawType,
      operation,
      part_ids: parts,
      location_id: locationId,
      details,
    };
    const key = JSON.stringify({
      ...input,
      part_ids: [...parts].sort((a, b) => a - b),
    });
    if (seen.has(key)) errors.push("Duplicate row in this file");
    seen.add(key);
    return [{ row: index + 2, input, errors }];
  });
  if (!rows.length)
    throw new Error(
      "The file has no tooling rows. Fill in the template first."
    );
  return rows;
}
